/* ---------------------------------------------------------------------------
   Derivations over the register. Nothing here mutates state; every function
   takes the raw records and returns view-ready data.
   --------------------------------------------------------------------------- */

import type {
  Bridge,
  BridgeView,
  Inspection,
  InspectionType,
  ModelSummary,
  RegisterDefect,
} from './types.ts';
import { defectCodeLabel } from './defects.ts';
import { elementLabel } from './elements.ts';
import { getRegister } from './register.ts';
import { distinct, metres, NOT_RECORDED, phrase, sumRecorded } from './recorded.ts';
import { inCycle, phaseOf } from './phase.ts';
import { resetsCycle, satisfiesCycle, triggerLabel } from './inspectionTypes.ts';
import { active } from '../tenant/registry.ts';

/* --------------------------- which model the record draws --------------------------- */

/**
 * Is there anything in this model to draw?
 *
 * THREE KINDS OF EVIDENCE, ANY ONE OF WHICH IS ENOUGH, because each of them can
 * be produced independently of the other two and any of them can be missed:
 *
 * - **A captured still** (`hasThumb`) — the modeller photographed its own canvas,
 *   so the scene rendered at least once.
 * - **Exported geometry** (`hasGeometry`) — a `.glb` reached R2, which the map and
 *   the inspector already draw from; it cannot exist without a rendered scene.
 * - **An imported mesh** (`imported === true`) — the saved state itself carries
 *   `hasImportedMesh`, sniffed server-side by `importedFlagOf`. This is the source's
 *   own geometry, and it is in the state whether or not anything has drawn it yet.
 *
 * `imported === null` is NOT evidence: it means the server could not tell, and an
 * "I cannot tell" must not be read as a yes.
 */
export function modelIsDrawable(m: ModelSummary): boolean {
  return m.hasThumb || m.hasGeometry || m.imported === true;
}

/**
 * Whether the geometry for a model can be drawn on THIS device yet.
 *
 * THE THING THIS EXISTS TO PREVENT is a wrong bridge that looks right. When the
 * modeller opens a model whose state says `hasImportedMesh` and finds no mesh in
 * this origin's IndexedDB, it does not stop: `App.LoadImportedCloudMesh` sets
 * `importedCloudMesh = null` and REBUILDS THE BRIDGE FROM ITS IMPORTED PARAMETERS.
 * Its own words for the result are that it "will not match the original exactly",
 * and nobody ever reads them — the alert is raised inside a frame sandboxed without
 * `allow-modals`, so it is swallowed.
 *
 * The visible sequence was therefore: the still, which is correct, then a
 * plausible and different bridge painted over it, with nothing saying which one to
 * believe. Mounting the viewport before the answer is known guarantees that, since
 * the answer needs an IndexedDB read and the mount happens in a paint.
 *
 *   - `'ready'`     — draw it. Either the mesh is here, or none is needed.
 *   - `'checking'`  — nobody has asked yet. Show the still and no frame; the still
 *                     is a photograph of the real thing and is the honest choice
 *                     while the answer is outstanding.
 *   - `'absent'`    — the triangles are not on this device. Say so; do not draw.
 *
 * A PARAMETRIC MODEL IS ALWAYS `'ready'`. It carries no mesh and needs none, being
 * rebuilt from its own parameters wherever it is opened — the rebuild is the point
 * rather than a substitution for something missing.
 */
export type MeshAvailability = 'ready' | 'checking' | 'absent';

export function meshAvailability(
  model: Pick<ModelSummary, 'id' | 'imported'>,
  /** What the local check found, or null for "not asked yet" — see modelMeshState. */
  meshState: { modelId: string; present: boolean } | null,
): MeshAvailability {
  if (model.imported !== true) return 'ready';
  if (meshState?.modelId !== model.id) return 'checking';
  return meshState.present ? 'ready' : 'absent';
}

/**
 * The model the record shows, or null for nothing to show.
 *
 * IT USED TO BE `find((m) => m.hasThumb)` AND THAT STOPPED BEING TRUE. The still
 * was once a safe proxy for "a model exists and has been rendered": the only way
 * to get a model was to build one in the modeller, and the modeller captured a
 * still on the same save that wrote the state. Two changes broke it, and together
 * they made a saved bridge permanently invisible:
 *
 * 1. The register now MINTS AN EMPTY MODEL of its own before an import, so a model
 *    can exist having never held a bridge at all.
 * 2. The modeller is no longer a destination, so the only thing that captures a
 *    still is the import's own hidden frame — and an import deliberately treats a
 *    missed capture as a non-failure ("the capture is skipped rather than failed if
 *    the canvas is not in a state to be photographed"). Nothing captures a later
 *    one, because there is no longer anywhere to open the model.
 *
 * So a still that did not arrive used to mean "wait a few seconds" and now means
 * "wait forever": the panel drew nothing, mounted no viewport, and reported "1
 * model saved, none rendered yet" indefinitely, over a model whose geometry the map
 * on the same screen was drawing quite happily.
 *
 * A still is still PREFERRED, because it is the poster that shows while the
 * viewport's 1.6 MB chunk loads — a model that has one is the nicest to show. It is
 * no longer required.
 */
export function heroModel(models: readonly ModelSummary[]): ModelSummary | null {
  return (
    models.find((m) => m.hasThumb) ??
    models.find((m) => modelIsDrawable(m)) ??
    null
  );
}

/**
 * Shorten a long superstructure description for the register's narrow column.
 *
 * Null in, null out. A record with no recorded superstructure has nothing to
 * shorten, and returning the dash here would put a dash inside a phrase that the
 * caller then has to take apart again — see `typeSpan` below, which drops the
 * whole clause instead.
 */
export function shortType(type: string | null): string | null {
  if (type == null || type.trim() === '') return null;
  return type
    .replace('PT single-cell box girder', 'PT box girder')
    .replace('PSC 3-webbed slab-deck, cast in situ', 'PSC slab-deck')
    .replace(/RC T-beam, \d+ girders/, 'RC T-beam');
}

const INSPECTION_TYPE_LABELS: Record<InspectionType, string> = {
  routine: 'Routine',
  principal: 'Principal',
  baseline: 'Baseline',
  special: 'Special',
  'post-event': 'Post-event',
  underwater: 'Underwater / NSTM',
};

/** Display label for an inspection tier, e.g. "routine" -> "Routine". */
export function inspectionTypeLabel(type: InspectionType): string {
  return INSPECTION_TYPE_LABELS[type];
}

/** The tier and, on a special or post-event inspection, what caused it: "Post-event · Flood". */
export function inspectionKindLabel(insp: Pick<Inspection, 'type' | 'trigger'>): string {
  const label = inspectionTypeLabel(insp.type);
  return insp.trigger ? `${label} · ${triggerLabel(insp.trigger)}` : label;
}

export function toView(b: Bridge): BridgeView {
  return {
    ...b,
    /* Both are phrases assembled from parts that may be missing, so both drop
       the clauses they have no figure for rather than printing "null / 12.4" or
       "· 28m" with nothing in front of it. A record with neither reads as the
       dash, which is what the register shows for everything else it does not
       know. */
    routeKm: phrase([b.route, b.km.toFixed(1)], ' / ') ?? NOT_RECORDED,
    typeSpan:
      phrase([shortType(b.type), b.length == null ? null : `${b.length.toFixed(0)}m`]) ??
      NOT_RECORDED,
    freeboard: b.deckRL != null && b.hfl != null ? Number((b.deckRL - b.hfl).toFixed(2)) : null,
  };
}

/** The whole register as views — from D1 once hydrated, the seed until then. */
export function allBridges(): BridgeView[] {
  return getRegister().map(toView);
}

/**
 * The record for that id, the first record where the id names none, or null
 * where the register is empty.
 *
 * THE FALLBACK TO THE FIRST RECORD IS DELIBERATE and is not this function being
 * lax: a link may name a structure this register has not got yet — a bookmark, a
 * shared URL, a device that has not hydrated — and landing such a reader on a
 * record rather than on nothing is the documented decision (see ROADMAP.md,
 * "A link may name a structure the register has not got yet"). The router keeps
 * the address honest afterwards.
 *
 * NULL IS THE CASE THAT FALLBACK CANNOT COVER, and it used to be written
 * `register[0]!` — a non-null assertion that is simply false when the register
 * is empty. Nothing caught it because every department bundled a seed, so the
 * register was never empty in a rendered frame. `pk-fwo` is configured with no
 * seed (FWO has not supplied a bridge list), and the first paint threw in the
 * nav pane on `b.km.toFixed(1)`.
 *
 * That is worth stating plainly because it was never a Pakistan bug: an empty
 * register is what EVERY department looks like on its first day, before the
 * first import lands. The assertion would have taken the whole application down
 * on day one of any new deployment, and on any device whose hydration returned
 * an empty register. It is now a type the callers have to answer for, which is
 * why the signature changed rather than the body gaining a silent guard.
 */
export function findBridge(id: string): BridgeView | null {
  const register = getRegister();
  const found = register.find((b) => b.id === id) ?? register[0];
  return found ? toView(found) : null;
}

/**
 * Distinct district names, in register order.
 *
 * Records with no district recorded contribute nothing — there is no chip for
 * them and no rollup row, because a filter labelled with the empty string is a
 * filter nobody can read. They are still in the register, still counted by every
 * total, and still reachable through the "All" chip, which is the one place the
 * two treatments have to agree. All 22 Quirino-Andaya records are in this state.
 */
export function districtNames(list: BridgeView[]): string[] {
  return distinct(list, (b) => b.district);
}

/**
 * The one-line geometry caption under a hero image or a still.
 *
 * "70.00 m over 2 spans (35.00 m typical) · RC T-beam". Written once because it
 * appeared verbatim at three call sites, and every one of them had to learn the
 * same four things when the register became able to say it does not know a
 * length, a span count or a superstructure:
 *
 *   · CLAUSES ARE DROPPED, NOT BLANKED. A caption reading "— m over — spans
 *     (— m typical) · —" is four dashes where a reader expected a sentence.
 *   · THE TYPICAL SPAN IS ARITHMETIC AND NEEDS BOTH ITS INPUTS. Length over
 *     spans with either missing is not a number, and with `spans` zero it is an
 *     infinity that prints as "Infinity m typical".
 *   · IT IS "TYPICAL", NOT "EACH". A mean span length on a structure with
 *     unequal spans is not any span, which is what the word is doing.
 *   · NULL WHERE NOTHING IS KNOWN, so the caller omits the line rather than
 *     printing an empty one.
 */
export function geometryCaption(b: Bridge): string | null {
  const spans =
    b.spans == null ? null : `over ${b.spans} span${b.spans === 1 ? '' : 's'}`;
  const typical =
    b.length != null && b.spans != null && b.spans > 0
      ? `(${(b.length / b.spans).toFixed(2)} m typical)`
      : null;
  const size = phrase([b.length == null ? null : metres(b.length), spans, typical], ' ');
  return phrase([size, b.type]);
}

/* --------------------------- rollups --------------------------- */

export interface DistrictRollup {
  name: string;
  count: number;
  totalLength: number;
  /** Share of the register this district carries, as a meter width. */
  share: string;
}

export function districtRollups(list: BridgeView[]): DistrictRollup[] {
  const total = list.length;
  return districtNames(list)
    .map((name) => {
      const set = list.filter((b) => b.district === name);
      return {
        name,
        count: set.length,
        totalLength: Math.round(sumRecorded(set, (b) => b.length).total),
        share: `${((set.length / total) * 100).toFixed(1)}%`,
      };
    })
    .sort((a, b) => b.count - a.count);
}

export interface RoadRollup {
  /** Null for a register that names its roads without numbering them — DPWH. */
  route: string | null;
  roadName: string;
  count: number;
  totalLength: number;
}

export function roadRollups(list: BridgeView[]): RoadRollup[] {
  const routes = Array.from(new Set(list.map((b) => b.route)));
  return routes
    .map((route) => {
      const set = list.filter((b) => b.route === route);
      return {
        route,
        roadName: set[0]!.roadName,
        count: set.length,
        totalLength: Math.round(sumRecorded(set, (b) => b.length).total),
      };
    })
    .sort((a, b) => b.count - a.count);
}

/** Bridges transcribed from a real DoR inventory sheet — have `inventoryCode` set. */
export function withInventorySheet(list: BridgeView[]): BridgeView[] {
  return list.filter((b) => b.inventoryCode != null);
}

/**
 * One figure in the overview's headline strip.
 *
 * `value` is a formatted string, not a number: a deck length is quoted to one
 * decimal and a count is not, and deciding that here keeps every tile reading
 * the same way rather than leaving each caller to pick a precision.
 */
export interface Kpi {
  label: string;
  value: string;
  unit: string;
  color: string;
}

/**
 * The four figures that come out of the register alone.
 *
 * Open defects is the fifth tile on screen and is not here, because it needs the
 * modeller's defect logs — which are per-structure network calls, not a
 * derivation. The overview appends it.
 */
export function kpis(list: BridgeView[]): Kpi[] {
  const totalLength = sumRecorded(list, (b) => b.length).total;

  const measured = list.filter((b) => b.freeboard != null);
  const meanFreeboard = measured.length
    ? measured.reduce((t, b) => t + b.freeboard!, 0) / measured.length
    : null;

  /* Counted once per structure: a structure whose routine visit AND principal
     inspection are both out is one structure to send somebody to. */
  const standings = list.map((b) => inspectionStanding(b));
  const due = standings.filter((s) => isOutstanding(s.worst)).length;
  /* Out of the structures that have a cycle: one being built is not a
     structure the count could have included (LC-13). */
  const cycled = standings.filter((s) => s.worst.status !== 'Not in service').length;

  return [
    { label: 'Structures', value: String(list.length), unit: 'records', color: 'var(--txt)' },
    { label: 'Deck length', value: totalLength.toFixed(1), unit: 'm', color: 'var(--txt)' },
    {
      label: 'Mean freeboard',
      value: meanFreeboard === null ? '—' : meanFreeboard.toFixed(1),
      unit: 'm',
      color: 'var(--txt)',
    },
    { label: 'Inspections due', value: String(due), unit: `of ${cycled}`, color: 'var(--txt)' },
  ];
}

/**
 * A structure's name without the word "Bridge".
 *
 * Every record in a bridge register is a bridge, so the suffix carries no
 * information and costs a chart its label width. Kept out of `BridgeView` on
 * purpose: this is a presentation choice for tight columns, not another name for
 * the structure.
 */
export function shortName(b: BridgeView): string {
  return stripBridgeSuffix(b.name);
}

/** As `shortName`, for a name that has been carried away from its record. */
export function stripBridgeSuffix(name: string): string {
  return name.replace(/\s+Bridge$/, '');
}

/* --------------------------- dates --------------------------- */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2025-03-02" -> "2 Mar 25". Returns "—" for an empty or unparseable date. */
export function prettyDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${String(d.getUTCFullYear()).slice(2)}`;
}

/** "13 Aug 2026" — the form a header or a footnote wants. */
export function mediumDate(d: Date): string {
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/** "13 August 2026" — the long form, for a sheet's issue line. */
export function longDate(d: Date): string {
  const MONTHS_LONG = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  return `${d.getDate()} ${MONTHS_LONG[d.getMonth()]} ${d.getFullYear()}`;
}

function addMonths(iso: string, months: number): string {
  const d = new Date(iso);
  d.setUTCMonth(d.getUTCMonth() + months);
  return d.toISOString().slice(0, 10);
}

/* --------------------------- inspection standing --------------------------- */

/**
 * Where a structure sits against its inspection cycles — plural, deliberately.
 *
 * DoR's *Guideline for Inspection and Maintenance of Bridge* puts two tiers on a
 * calendar: routine inspections at least annually, and principal inspections
 * every five to eight years. Two more are dated here since LC-60: the baseline
 * inspection, due once on the handover date, and the underwater inspection,
 * on the interval set for the structure where one is set. Special and
 * post-event inspections are triggered by an event rather than a calendar and
 * get no cycle; they are recorded, and they count as "somebody looked at it",
 * and that is all.
 *
 * **Each cycle is dated from the latest inspection that resets it.** This used
 * to be one cycle dated from the latest inspection of any tier, which got both
 * halves wrong: an underwater inspection silently reset the routine clock, and a
 * principal inspection six years overdue was invisible behind a routine one done
 * last month. A principal inspection DOES reset the routine clock (LC-61): the
 * close-up visit sees everything the walk-over would. The rules are
 * `resetsCycle` in src/data/inspectionTypes.ts.
 *
 * **Only what was signed off counts** (LC-61). A draft, an entry in review and
 * one not yet synced are on the record and satisfy no cycle — `satisfiesCycle`.
 *
 * **Works completed make a special inspection due** (LC-82): once, on the
 * completion date, until a special inspection with trigger `works` is on file.
 * It is dated like the baseline and is not a cycle; nothing else here changes.
 */
export type InspectionCycle = 'routine' | 'principal' | 'baseline' | 'underwater' | 'special';

export type InspectionStatus =
  | 'Overdue'
  | 'Never inspected'
  | 'Due soon'
  | 'Current'
  | 'Not yet due'
  /* Handed over, and the baseline inspection not yet on file (LC-60). */
  | 'Baseline due'
  /* Works completed, and the special inspection after them not yet on file (LC-82). */
  | 'Special due'
  /* Planned, under construction or closed (LC-13): there is no cycle to be
     behind on. It is not an alarm, and it is not "Never inspected", which a
     bridge being built used to be ranked as — the worst rank there is. */
  | 'Not in service';

export interface CycleStanding {
  cycle: InspectionCycle;
  /** Latest inspection that reset THIS cycle, or null if there has never been one. */
  last: Inspection | null;
  /** ISO date this cycle next falls due, or null where it cannot be dated at all. */
  due: string | null;
  status: InspectionStatus;
  /** Rank for a monochrome tag: 4 is the most urgent. */
  rank: 1 | 2 | 3 | 4;
}

export interface InspectionStanding {
  routine: CycleStanding;
  principal: CycleStanding;
  /** The baseline inspection, while a handed-over bridge has none on file; else null. */
  baseline: CycleStanding | null;
  /** The special inspection after works completed, while none is on file; else null (LC-82). */
  special: CycleStanding | null;
  /** The underwater cycle, where the structure has an interval set; else null. */
  underwater: CycleStanding | null;
  /**
   * Whichever cycle is in the worst state.
   *
   * What a single chip, a single count and the register's sort all use, so that
   * a structure appears once rather than twice — the screens that have room
   * show every cycle beside it.
   */
  worst: CycleStanding;
  /** The latest inspection of ANY tier: when anyone last looked at the structure. */
  latest: Inspection | null;
  /** Whether the shorter intervals apply: restricted, or in poor condition (LC-61). */
  shortened: boolean;
}

/** How near a cycle's edge counts as "due soon". Four months of warning. */
const DUE_SOON_DAYS = 120;

/**
 * The interval for each calendar cycle, in months — the department's
 * (`TenantProfile.inspection`, LC-61), shortened for a restricted bridge or one
 * whose latest condition is poor.
 *
 * Routine is annual, which the guideline states as a minimum and every
 * profile treats as the interval.
 *
 * **Principal is a policy choice, not a fact.** The guideline says five to eight
 * years — a range, and which end applies to a given structure is a judgement
 * about its type, age and exposure that the register does not carry. Sixty
 * months is the conservative end of that range, chosen so the register flags a
 * structure early rather than late. Widening it is a decision for whoever owns
 * the inspection programme, made in the profile, not a bug fix.
 */
function cycleMonths(b: Bridge, cycle: 'routine' | 'principal'): { months: number; shortened: boolean } {
  const policy = active().inspection;
  const shortened = phaseOf(b).restricted || poorCondition(b, policy.poorFrom);
  if (cycle === 'routine') return { months: shortened ? policy.shortRoutineMonths : policy.routineMonths, shortened };
  return { months: shortened ? policy.shortPrincipalMonths : policy.principalMonths, shortened };
}

/** Whether the latest signed-off inspection rated the structure poor: at or past `poorFrom` of its scale. */
function poorCondition(b: Bridge, poorFrom: number): boolean {
  const latest = b.inspections.reduce<Inspection | null>(
    (best, insp) => (satisfiesCycle(insp) && (best === null || insp.date > best.date) ? insp : best),
    null,
  );
  if (latest === null) return false;
  const top = scaleMax(latest.scale);
  return top !== null && latest.rating / top >= poorFrom;
}

/**
 * When a cycle starts counting for a structure that has never had one of its
 * tier: the handover date, where the bridge was handed over through this
 * register (LC-11); the date it was recorded as going into service, where it
 * was recorded (LC-84) — January of the year where only the year was; and the
 * year it was completed otherwise.
 *
 * A structure built in 2020 with no principal inspection on file is not in good
 * standing, it is five years into a five-year cycle nobody has started. But a
 * structure built last year is genuinely not due yet, and saying otherwise would
 * be a false alarm on a screen whose whole value is that its alarms mean
 * something.
 *
 * January of the year is used where there is no handover, because the register
 * then only carries the year, and the earlier date is the conservative one.
 */
function cycleStartFor(b: Bridge): string | null {
  if (b.handover?.completedOn) return b.handover.completedOn;
  if (b.inServiceOn) return /^\d{4}$/.test(b.inServiceOn) ? `${b.inServiceOn}-01-01` : b.inServiceOn;
  /* No recorded year is the same answer as an implausible one: there is no date
     to start a cycle from, so the structure has no cycle rather than a cycle
     beginning at year zero. Every DPWH record is in this state. */
  if (b.year == null || !Number.isFinite(b.year) || b.year < 1800) return null;
  return `${b.year}-01-01`;
}

/** The latest signed-off inspection of the kinds that reset a cycle. */
function latestResetting(b: Bridge, resets: (type: InspectionType) => boolean): Inspection | null {
  return b.inspections.reduce<Inspection | null>(
    (best, insp) =>
      resets(insp.type) && satisfiesCycle(insp) && (best === null || insp.date > best.date) ? insp : best,
    null,
  );
}

const RESETS: Readonly<Record<InspectionCycle, (type: InspectionType) => boolean>> = {
  routine: (t) => resetsCycle(t, 'routine'),
  principal: (t) => resetsCycle(t, 'principal'),
  baseline: (t) => t === 'baseline',
  underwater: (t) => t === 'underwater',
  special: (t) => t === 'special',
};

function cycleStanding(b: Bridge, cycle: InspectionCycle, months: number, now: Date): CycleStanding {
  const last = latestResetting(b, RESETS[cycle]);
  /* No cycle runs until the bridge is in service (LIFECYCLE.md §3). Closed is
     here too: a closed bridge has special inspections only. A bridge with no
     phase recorded is in service, and falls through. */
  if (!inCycle(phaseOf(b))) return { cycle, last, due: null, status: 'Not in service', rank: 1 };
  const from = last ? last.date : cycleStartFor(b);

  /* Nothing of this tier on file AND no completion year to date the first one
     from. Ranks with overdue rather than below it, which is the rule this file
     has always followed: a structure nobody can account for is not in better
     standing than one whose visit slipped by a month. */
  if (from === null) return { cycle, last: null, due: null, status: 'Never inspected', rank: 4 };

  const due = addMonths(from, months);
  const days = (new Date(due).getTime() - now.getTime()) / 86_400_000;

  /* `last === null` splits each side of the line in two. Past it, "Never
     inspected" says more than "Overdue" does — there is no lapsed schedule
     here, there is no schedule. Before it, "Not yet due" says what "Current"
     would quietly misstate: nobody has carried out this tier of inspection, and
     nobody was yet required to. */
  if (days < 0) return { cycle, last, due, status: last ? 'Overdue' : 'Never inspected', rank: 4 };
  if (days < DUE_SOON_DAYS) return { cycle, last, due, status: 'Due soon', rank: 3 };
  return { cycle, last, due, status: last ? 'Current' : 'Not yet due', rank: 1 };
}

/**
 * The baseline inspection of a handed-over bridge (LC-60): due on the handover
 * date, and null — nothing to show — once one is on file, or where the bridge
 * was never handed over here.
 */
function baselineStanding(b: Bridge, now: Date): CycleStanding | null {
  const due = b.handover?.completedOn ?? null;
  if (due === null || !inCycle(phaseOf(b))) return null;
  if (latestResetting(b, RESETS.baseline) !== null) return null;
  const late = new Date(due).getTime() < now.getTime();
  return { cycle: 'baseline', last: null, due, status: 'Baseline due', rank: late ? 4 : 3 };
}

/**
 * The special inspection works completed make due (LC-82): on the completion
 * date, and null once a signed-off special inspection with trigger `works` is
 * on file from that day on. A closed bridge takes special inspections, so it is
 * due there too. Never a baseline: the bridge was not handed over again.
 */
function specialStanding(b: Bridge, now: Date): CycleStanding | null {
  const due = b.worksCompleted?.completedOn ?? null;
  const p = phaseOf(b);
  if (due === null || (p.phase !== 'in-service' && p.phase !== 'closed')) return null;
  const done = b.inspections.some(
    (i) => i.type === 'special' && i.trigger === 'works' && satisfiesCycle(i) && i.date >= due,
  );
  if (done) return null;
  const late = new Date(due).getTime() < now.getTime();
  return { cycle: 'special', last: null, due, status: 'Special due', rank: late ? 4 : 3 };
}

/** The more pressing of two cycles: by rank, then by whichever falls due first. */
function worseOf(a: CycleStanding, b: CycleStanding): CycleStanding {
  if (a.rank !== b.rank) return a.rank > b.rank ? a : b;
  if (a.due === null) return a;
  if (b.due === null) return b;
  return a.due <= b.due ? a : b;
}

/**
 * Whether a cycle wants attention — what "Inspections due" counts.
 *
 * "Due soon" counts, which is the point of warning about it. "Not yet due" does
 * not: a cycle that has not come round is not an outstanding item, and counting
 * it would make every new structure look neglected on the day it was added.
 */
export function isOutstanding(c: CycleStanding): boolean {
  return c.status !== 'Current' && c.status !== 'Not yet due' && c.status !== 'Not in service';
}

export function inspectionStanding(b: Bridge, now: Date = new Date()): InspectionStanding {
  const r = cycleMonths(b, 'routine');
  const routine = cycleStanding(b, 'routine', r.months, now);
  const principal = cycleStanding(b, 'principal', cycleMonths(b, 'principal').months, now);
  const baseline = baselineStanding(b, now);
  const special = specialStanding(b, now);
  const underwater =
    b.underwaterMonths != null && b.underwaterMonths > 0
      ? cycleStanding(b, 'underwater', b.underwaterMonths, now)
      : null;
  // The register does not promise an order, so find the latest rather than
  // trusting position.
  const latest = b.inspections.reduce<Inspection | null>(
    (best, insp) => (best === null || insp.date > best.date ? insp : best),
    null,
  );
  const worst = [baseline, special, underwater].reduce<CycleStanding>(
    (w, c) => (c === null ? w : worseOf(w, c)),
    worseOf(routine, principal),
  );
  return { routine, principal, baseline, special, underwater, worst, latest, shortened: r.shortened };
}

const CYCLE_NAME: Readonly<Record<InspectionCycle, string>> = {
  routine: 'Routine',
  principal: 'Principal',
  baseline: 'Baseline',
  underwater: 'Underwater',
  special: 'Special',
};

/**
 * One line describing where a cycle stands, for a chip's title or a tooltip.
 *
 * Here rather than in the screens because four of them say this and they were
 * saying it four slightly different ways — all of them the words "Next routine
 * inspection due", including on the principal cycle, which was wrong before
 * these were separate cycles and would have stayed wrong after.
 */
export function cycleLabel(c: CycleStanding): string {
  const name = CYCLE_NAME[c.cycle];
  if (c.status === 'Not in service') return `${name}: no cycle until the structure is in service`;
  if (c.due === null) {
    return `${name}: none on file, and no in-service date or completion year to date the first one from — due now`;
  }
  const when = prettyDate(c.due);
  switch (c.status) {
    case 'Baseline due':
      return `${name}: due on the handover date, ${when}`;
    case 'Special due':
      return `${name}: due on the works completion date, ${when}`;
    case 'Overdue':
      return `${name}: overdue since ${when}`;
    case 'Never inspected':
      return `${name}: none on file — the first was due ${when}`;
    case 'Due soon':
      return `${name}: due ${when}`;
    case 'Not yet due':
      return `${name}: none on file — the first is due ${when}`;
    case 'Current':
      return `${name}: next due ${when}`;
  }
}

/** Chip text for one cycle, e.g. "Routine · overdue". */
export function cycleChipLabel(c: CycleStanding): string {
  if (c.status === 'Baseline due') return 'Baseline due';
  if (c.status === 'Special due') return 'Special due';
  return `${CYCLE_NAME[c.cycle]} · ${c.status.toLowerCase()}`;
}

/** Every cycle, one line each — the whole standing as a tooltip. */
export function standingLabel(s: InspectionStanding): string {
  return [s.baseline, s.special, s.routine, s.principal, s.underwater]
    .filter((c): c is CycleStanding => c !== null)
    .map(cycleLabel)
    .join('\n');
}

/** The register ordered by how soon each structure is due, most pressing first. */
export function byInspectionDue(list: BridgeView[], now: Date = new Date()): {
  bridge: BridgeView;
  standing: InspectionStanding;
}[] {
  return list
    .map((bridge) => ({ bridge, standing: inspectionStanding(bridge, now) }))
    .sort((a, b) => {
      // Ordered by whichever of a structure's two cycles is in the worse state,
      // so one structure takes one place in the list.
      const x = a.standing.worst;
      const y = b.standing.worst;
      /* A structure with no cycle is last, not first: its undated `due` is
         not the undateable one below. */
      const xOut = x.status === 'Not in service';
      const yOut = y.status === 'Not in service';
      if (xOut || yOut) return xOut === yOut ? 0 : xOut ? 1 : -1;
      // Undateable at all is the most pressing thing on the list, not the least.
      if (x.due === null) return y.due === null ? 0 : -1;
      if (y.due === null) return 1;
      if (x.due !== y.due) return x.due < y.due ? -1 : 1;
      return y.rank - x.rank;
    });
}

/* --------------------------- chart series --------------------------- */

export interface BarRow {
  bridge: BridgeView;
  /** Value as a percentage of the widest bar in the set, e.g. "62%". */
  width: string;
  label: string;
}

/**
 * Freeboard by structure, scaled to the largest margin in the set.
 *
 * Only structures with both a deck level and an HFL on file appear: a bar of
 * width zero for an unrecorded freeboard would read as "no clearance", which is
 * the opposite of "not measured".
 */
export function freeboardBars(list: BridgeView[]): BarRow[] {
  const withBoth = list.filter((b) => b.freeboard != null);
  const max = withBoth.reduce((m, b) => Math.max(m, b.freeboard!), 0);
  /* Register order, not descending. The chart is read alongside every other
     panel on the screen, and a set of bars that reorders itself as levels are
     surveyed makes "the third one down" mean a different bridge each visit. */
  return withBoth.map((bridge) => ({
    bridge,
    width: max > 0 ? `${((bridge.freeboard! / max) * 100).toFixed(1)}%` : '0%',
    label: bridge.freeboard!.toFixed(1),
  }));
}

/** The structure with the widest freeboard margin, for the chart's scale note. */
export function widestFreeboard(list: BridgeView[]): BridgeView | null {
  return list
    .filter((b) => b.freeboard != null)
    .reduce<BridgeView | null>((m, b) => (m === null || b.freeboard! > m.freeboard! ? b : m), null);
}

export interface CountRow {
  label: string;
  count: number;
  width: string;
}

/** Superstructure families, keyed on the description up to its first comma. */
export function typeMix(list: BridgeView[]): CountRow[] {
  const counts = new Map<string, number>();
  /* A structure with no recorded superstructure is not a family of its own and
     is not folded into the largest one — it is left out of the mix entirely, and
     the caller's own "records" count is what says how many that was. A bar
     labelled with the empty string is a bar nobody can read. */
  for (const b of list) {
    if (b.type == null || b.type.trim() === '') continue;
    const family = b.type.split(',')[0]!.trim();
    counts.set(family, (counts.get(family) ?? 0) + 1);
  }
  const max = Math.max(...counts.values(), 1);
  return Array.from(counts, ([label, count]) => ({
    label,
    count,
    width: `${((count / max) * 100).toFixed(1)}%`,
  })).sort((a, b) => b.count - a.count);
}

export interface YearColumn {
  year: number;
  count: number;
  /** Height in px, against a 74px tallest column. */
  height: number;
}

/** Completions by year, oldest first. */
export function yearHistogram(list: BridgeView[]): YearColumn[] {
  const counts = new Map<number, number>();
  /* Records with no year recorded get no column. A histogram of completions
     cannot place a structure whose completion nobody wrote down, and a column at
     year 0 would be a hundred-year-wide empty axis on the DPWH register. */
  for (const b of list) if (b.year != null) counts.set(b.year, (counts.get(b.year) ?? 0) + 1);
  const max = Math.max(...counts.values(), 1);
  return Array.from(counts, ([year, count]) => ({
    year,
    count,
    height: Math.round((count / max) * 74),
  })).sort((a, b) => a.year - b.year);
}

/* --------------------------- the route schematic --------------------------- */

export interface RouteTick {
  /** Position along the strip, as a percentage. */
  at: string;
  km: number;
}

export interface RouteStrip {
  /**
   * The road's code, or null where the register names its roads without
   * numbering them. `roadName` is always there and is what the strip is titled
   * with; this is the code beside it, where there is one.
   */
  route: string | null;
  roadName: string;
  count: number;
  /** Deck carried on this stretch, in whole metres. */
  totalLength: number;
  /**
   * The window the axis draws, in km — NOT from zero.
   *
   * It used to run 0 to the last structure, which is right for a register whose
   * chainages start near zero and useless for one whose do not: DPWH's Samar
   * bridges sit between km 796 and 887, so an axis from 0 to 1626 spent 95% of
   * its width on road this register holds nothing on and squeezed all 34
   * structures into one illegible clump.
   *
   * These are the first and last structure's chainage, rounded — what the
   * heading prints. The axis the markers are positioned against is a few per
   * cent wider at each end so the end labels, which are centred on their point,
   * do not hang off the edge; that margin is a layout detail and is not exposed.
   */
  from: number;
  to: number;
  ticks: RouteTick[];
  /** Every structure on the stretch, in chainage order, placed on the axis. */
  markers: { bridge: BridgeView; at: string }[];
  /**
   * The same structures as stops: one per place on the road, a parallel deck
   * folded into its twin's. What the strip and the list beside it draw.
   */
  stops: RouteStop[];
  /**
   * The stretch divided by the tenant's first grouping — DPWH's contract
   * packages — in chainage order. Empty where that grouping does not divide it
   * (one value along the whole stretch) or leaves a stop without a value, so a
   * strip is either fully sectioned or not sectioned at all.
   */
  sections: RouteSection[];
}

export interface RouteStop {
  /**
   * The structure, then its parallel twin where the register records one beside
   * it. Never empty.
   */
  bridges: BridgeView[];
  /** Position along the strip, as a percentage. */
  at: string;
}

export interface RouteSection {
  /** The grouping's value: "A1", a district name. */
  label: string;
  /** Where its band starts and ends on the strip, as percentages. */
  from: string;
  to: string;
  stops: RouteStop[];
  /** Structures in it, twins counted — what the register would count. */
  count: number;
}

/** How many labelled ticks a strip carries, whatever its length. */
const TICKS = 6;

/**
 * A gap in chainage wide enough to split one road into two strips.
 *
 * A strip is a diagram of where structures sit along a road, and a stretch with
 * nothing on it tells the reader nothing while compressing the stretches that do.
 * DPWH's register is the case that forced this: Samar and Agusan del Sur are both
 * "Maharlika Highway" — genuinely one continuous road with one continuous
 * chainage — but this register covers km 796–887 of it and km 1246–1377, with
 * 359 km of nothing between. Drawn as one strip that is 86 structures in two
 * clumps at either end; drawn as two it is legible.
 *
 * 25 km, and the number is a judgement rather than a finding: it is wider than
 * any gap inside a stretch this register actually holds (the widest is 17.4 km,
 * between the A4 and A5 packages) and far narrower than the gaps between
 * stretches. A road whose structures are more than 25 km apart is one where the
 * reader cannot usefully compare neighbours anyway.
 */
const STRIP_BREAK_KM = 25;

/**
 * How close a parallel deck sits to its twin, in km, to be drawn as one stop.
 *
 * DPWH records most twins at the identical station and one (Anagasian) 3 m
 * apart. 50 m is generous for that and well short of the nearest two distinct
 * structures on the register — Hubang and Adlayan, 91 m apart.
 */
const TWIN_KM = 0.05;

const PARALLEL = /\bparallel\b/i;

/**
 * Round tick steps, smallest first. The scale a chainage is read off.
 *
 * WHOLE KILOMETRES ONLY, because the labels are printed as integers — a 2.5 km
 * step would put "253, 255, 258, 260, 263" on the axis, which reads as five
 * arbitrary numbers rather than a scale.
 *
 * FINER THAN THE USUAL 1-2-5 LADDER, and 3, 4, 25 and 30 are why. Six ticks
 * across the Quirino stretch's 13 km wants a step of 2.2; on a 1-2-5 ladder that
 * rounds to 5 and the axis carries two labels. With 3 in the ladder it carries
 * five. Across Agusan del Sur's 130 km it wants 21.8, which 1-2-5 rounds to 50
 * — three labels — where 25 gives six.
 */
const TICK_STEPS = [1, 2, 3, 4, 5, 10, 20, 25, 30, 40, 50, 100, 200, 250, 500, 1000] as const;

/** The smallest round step at or above `raw`. */
function niceStep(raw: number): number {
  if (!Number.isFinite(raw) || raw <= 0) return 1;
  return TICK_STEPS.find((step) => step >= raw) ?? TICK_STEPS[TICK_STEPS.length - 1]!;
}

/**
 * Fold each parallel deck into its twin, in chainage order.
 *
 * A twin is the neighbour within `TWIN_KM` where exactly one of the two is named
 * a parallel — so "Andanan" and "Andanan Parallel" are one stop, and "Wawa
 * Parallel", which has no unnamed twin on the register, stays a stop of its own.
 * The unnamed one leads. Indices are into `on`.
 */
function pairTwins(on: readonly BridgeView[]): number[][] {
  const out: number[][] = [];
  for (let i = 0; i < on.length; i++) {
    const a = on[i]!;
    const b = on[i + 1];
    if (b && b.km - a.km <= TWIN_KM && PARALLEL.test(a.name) !== PARALLEL.test(b.name)) {
      out.push(PARALLEL.test(a.name) ? [i + 1, i] : [i, i + 1]);
      i++;
    } else {
      out.push([i]);
    }
  }
  return out;
}

/**
 * Runs of stops sharing the tenant's first grouping, with the band each covers
 * on the strip — split halfway between the last stop of one and the first of
 * the next. Empty unless every stop has a value and there are at least two runs.
 */
function sectionsOf(stops: RouteStop[], fractions: readonly number[]): RouteSection[] {
  const grouping = active().groupings[0];
  if (!grouping || stops.length === 0) return [];
  const values = stops.map((stop) => grouping.of(stop.bridges[0]!));
  if (values.some((v) => v == null || v === '')) return [];

  const runs: { label: string; first: number; last: number }[] = [];
  values.forEach((v, i) => {
    const run = runs[runs.length - 1];
    if (run && run.label === v) run.last = i;
    else runs.push({ label: v!, first: i, last: i });
  });
  if (runs.length < 2) return [];

  const pct = (f: number) => `${(f * 100).toFixed(2)}%`;
  return runs.map((run, r) => {
    const members = stops.slice(run.first, run.last + 1);
    const prev = runs[r - 1];
    const next = runs[r + 1];
    return {
      label: run.label,
      from: pct(prev ? (fractions[prev.last]! + fractions[run.first]!) / 2 : 0),
      to: pct(next ? (fractions[run.last]! + fractions[next.first]!) / 2 : 1),
      stops: members,
      count: members.reduce((n, stop) => n + stop.bridges.length, 0),
    };
  });
}

/**
 * Structures placed by chainage along the road that carries them.
 *
 * A strip diagram, not a map. Only one record in the register has coordinates,
 * so there is nothing to plot geographically — but chainage is on every record,
 * and "which bridges are on this road, in what order, how far apart" is most of
 * what anyone wants a map for here.
 *
 * The axis runs a little past the first and last structure so the end markers
 * are never jammed against the edge. Nothing here labels a marker: names go in
 * the list under the strip, which has room for them however crowded the road.
 */
export function routeStrips(list: BridgeView[]): RouteStrip[] {
  /* Grouped in one pass rather than filtering the whole register once per
     route. Routes saturate at 229 on DoR's network, so the old form was a
     229x constant on a full scan — measured as the largest cost on the network
     screen at scale (`npm run scale`). Insertion order is preserved, which is
     what kept the strips in register order before. */
  const byRoute = new Map<string, BridgeView[]>();
  /* KEYED ON THE ROAD NAME WHERE THERE IS NO CODE. A route strip is a diagram of
     one road, and a register that names its roads without numbering them — the
     whole DPWH register — has roads to draw all the same. Falling back to the
     name rather than skipping keeps three highway sections on screen instead of
     none; falling back to the name rather than to the empty string keeps them
     from collapsing into one strip called "". */
  for (const b of list) {
    const key = b.route ?? b.roadName;
    const bucket = byRoute.get(key);
    if (bucket) bucket.push(b);
    else byRoute.set(key, [b]);
  }

  /* ONE ROAD CAN BE SEVERAL STRIPS. Split where the register holds nothing for
     more than STRIP_BREAK_KM, so a road this project covers in two distant
     stretches is drawn as two legible diagrams rather than one where both
     stretches are clumps at either end. See the note on the constant. */
  const stretches: BridgeView[][] = [];
  for (const members of byRoute.values()) {
    const on = [...members].sort((a, b) => a.km - b.km);
    let run: BridgeView[] = [on[0]!];
    for (let i = 1; i < on.length; i++) {
      if (on[i]!.km - on[i - 1]!.km > STRIP_BREAK_KM) {
        stretches.push(run);
        run = [];
      }
      run.push(on[i]!);
    }
    stretches.push(run);
  }

  return stretches
    .map((on) => {
      const first = on[0]!.km;
      const last = on[on.length - 1]!.km;

      /* THE AXIS IS A WINDOW ON THE STRUCTURES, NOT A RULER FROM ZERO. Snapped
         outward to whole tick steps so the labels are round numbers and the end
         markers have somewhere to sit instead of hanging off the edge.

         A stretch with one structure — or several at the same chainage, which
         happens wherever a parallel bridge shares its twin's station — has no
         span to divide, and `niceStep(0)` would be a division by zero dressed up
         as a layout. It gets a nominal window centred on the point, which is the
         only honest thing a scale can show for a single reading. */
      const span = last - first;

      /* NEVER FINER THAN A KILOMETRE, because the tick labels are whole
         kilometres. `niceStep` will happily return 0.2 for two structures 1 km
         apart, and then seven ticks round to "6, 6, 6, 6, 7, 7, 7" — a scale
         with the same number on it four times, at four different places. */
      const step = Math.max(1, niceStep(Math.max(span, 1) / TICKS));

      /* THE WINDOW IS THE STRUCTURES, AND THE TICKS FALL INSIDE IT. An earlier
         attempt snapped the window itself out to whole tick steps, which put
         round numbers at both ends and threw away up to a quarter of the axis
         doing it — the Agusan stretch runs to km 1377 and got an axis to 1500.
         So the frame is the first and last structure plus a margin, and the
         ticks are whatever round numbers land within it. An axis whose leftmost
         label is not at its leftmost pixel is what every other chart looks like.

         The margin is for the LABELS, which are centred on their point
         (`translateX(-50%)`) and would otherwise hang half off each end. 4% of
         the span is about half a label on a desk-width strip.

         A stretch with no span at all — one structure, or several sharing a
         station, which happens wherever a parallel bridge is recorded at its
         twin's chainage — has nothing to take a percentage of, so it gets a
         nominal window of one tick step either side, snapped so its labels are
         still round. */
      const pad = span > 0 ? span * 0.04 : (step * TICKS) / 2;
      const windowFrom = span > 0 ? first - pad : Math.floor((first - pad) / step) * step;
      const windowTo = span > 0 ? last + pad : windowFrom + step * TICKS;
      const width = windowTo - windowFrom;

      const ticks: RouteTick[] = [];
      for (
        let km = Math.ceil(windowFrom / step) * step;
        km <= windowTo + 1e-9;
        km += step
      ) {
        ticks.push({
          at: `${(((km - windowFrom) / width) * 100).toFixed(2)}%`,
          km: Math.round(km),
        });
      }

      const fractions = on.map((b) => (b.km - windowFrom) / width);
      const pairs = pairTwins(on);
      const stops: RouteStop[] = pairs.map((idx) => ({
        bridges: idx.map((i) => on[i]!),
        at: `${(fractions[idx[0]!]! * 100).toFixed(2)}%`,
      }));

      return {
        route: on[0]!.route,
        roadName: on[0]!.roadName,
        count: on.length,
        totalLength: Math.round(sumRecorded(on, (b) => b.length).total),
        /* THE STRUCTURES' OWN EXTENT, rounded, for the heading — not the padded
           window the positions are computed against. "km 796–887" is what
           somebody wants to read; the 4% margin either side is a layout detail
           and saying so in the heading would be noise. */
        from: Math.round(first),
        to: Math.round(last),
        ticks,
        markers: on.map((bridge, i) => ({
          bridge,
          at: `${(fractions[i]! * 100).toFixed(2)}%`,
        })),
        stops,
        sections: sectionsOf(stops, pairs.map((idx) => fractions[idx[0]!]!)),
      };
    })
    /* Tie-broken on the road NAME rather than the code, because the code is
       what a register without route numbers does not have — sorting on it would
       leave the DPWH strips in map-insertion order while looking deliberate.
       Then on chainage, so two stretches of one road read in the order somebody
       driving it would meet them. */
    .sort(
      (a, b) =>
        b.count - a.count || a.roadName.localeCompare(b.roadName) || a.from - b.from,
    );
}

/* --------------------------- defects --------------------------- */

/** Severity bands, most serious first. Matches the modeller's own vocabulary. */
/**
 * The top of a rating scale, as a number — 4 from "0-4", 10 from "0-10 scale".
 *
 * Read out of the string rather than stored, because the string is what the
 * inventory sheet said and sheets are not consistent about it: the "Add
 * inspection" dialog writes "0-4", older records in D1 carry "0-4 scale", and
 * anything transcribed from paper may use an en dash. A scale this cannot read
 * returns null, and the caller shows the number on its own rather than inventing
 * a denominator for it.
 *
 * Only the upper bound is returned. Every scale a DoR sheet uses starts at zero,
 * and a rating is read against the top of its range.
 */
export function scaleMax(scale: string): number | null {
  const match = /(\d+)\s*[-–—]\s*(\d+)/.exec(scale);
  if (!match) return null;
  const low = Number(match[1]);
  const high = Number(match[2]);
  return high > low ? high : null;
}

export const SEVERITIES = ['Critical', 'Severe', 'Moderate', 'Minor'] as const;

export type Severity = (typeof SEVERITIES)[number];

/**
 * Rank for a monochrome tag: 4 is the most serious.
 *
 * Four steps of one hue rather than four colours. Severity is an ordering, and
 * green/amber/orange/red reads as four unrelated verdicts — a reader has to
 * remember which of orange and red is worse, where a tonal ramp simply gets
 * darker.
 */
export function severityRank(severity: string): 1 | 2 | 3 | 4 {
  switch (severity) {
    case 'Critical':
      return 4;
    case 'Severe':
      return 3;
    case 'Moderate':
      return 2;
    default:
      return 1;
  }
}

/** A defect and the structure it was logged against. */
/**
 * A register defect with the structure's name attached, ready to list.
 *
 * The same shape the overview already draws the modeller's defects in, so one
 * card renders either. `where` collapses the two optional location fields into
 * the one line a list can show: the modeller component if this defect came from
 * a model, otherwise the element, otherwise nothing — which is a real state,
 * since a defect somebody saw is worth recording even when they could not say
 * precisely where.
 */
export interface LocatedRegisterDefect extends RegisterDefect {
  bridgeName: string;
  /** Human label for the category code, e.g. "Scouring". */
  label: string;
  /** Where on the structure, in words, or '' if it was not recorded. */
  where: string;
}

export function locateDefects(
  defects: readonly RegisterDefect[],
  list: BridgeView[],
): LocatedRegisterDefect[] {
  const names = new Map(list.map((b) => [b.id, b.name]));
  return defects
    .filter((d) => names.has(d.bridgeId))
    .map((d) => ({
      ...d,
      bridgeName: names.get(d.bridgeId) ?? d.bridgeId,
      label: defectCodeLabel(d.code),
      where: d.component ?? (d.element ? elementLabel(d.element) : ''),
    }));
}

/**
 * Open and resolved defects per severity band.
 *
 * Counts every defect it is given rather than filtering to the open ones — the
 * caller decides what "worth counting" means, and the overview's headline and the
 * per-structure breakdown do not agree about it.
 */
export function registerSeverityCounts(
  defects: readonly RegisterDefect[],
): Record<Severity, number> {
  const out = { Critical: 0, Severe: 0, Moderate: 0, Minor: 0 } as Record<Severity, number>;
  for (const d of defects) {
    if ((SEVERITIES as readonly string[]).includes(d.severity)) out[d.severity as Severity] += 1;
  }
  return out;
}

